import { CopyPoolsUIState } from '../state'

/**
 * Which asset and which copy variant the Review preview is currently showing for
 * a dynamic row.
 *
 * A dynamic ad has no single rendering — Facebook mixes one variant from each
 * pool with one asset at delivery. This pins one such combination so the AE can
 * step through a pool while holding the rest fixed and actually see each variant
 * in an ad card. It is a *view* control: deliberately never persisted, never sent
 * to Facebook, and never part of the manifest. See `CopyPoolEditor`.
 */
export interface DynamicSelection {
  asset: number
  primaryTexts: number
  headlines: number
  descriptions: number
  callToActions: number
}

export const DEFAULT_DYNAMIC_SELECTION: DynamicSelection = {
  asset: 0,
  primaryTexts: 0,
  headlines: 0,
  descriptions: 0,
  callToActions: 0,
}

/** The pool-backed keys, i.e. every key but `asset`. */
const POOL_KEYS = ['primaryTexts', 'headlines', 'descriptions', 'callToActions'] as const

/** Largest valid index into a list of `length`, or 0 when the list is empty. */
const clampIndex = (index: number, length: number) =>
  Math.min(Math.max(Math.trunc(index) || 0, 0), Math.max(length - 1, 0))

/**
 * Brings every index back into range for the pools/previews as they are *now*.
 *
 * Required rather than defensive: the AE can delete the very variant that's
 * pinned, which strands the index past the end of its pool. The preview and the
 * editor's highlight both resolve through this, so they can never disagree about
 * which variant is showing.
 */
export function clampSelection(
  selection: DynamicSelection,
  pools: CopyPoolsUIState | undefined,
  previewCount: number
): DynamicSelection {
  const clamped = { ...DEFAULT_DYNAMIC_SELECTION, asset: clampIndex(selection.asset, previewCount) }
  for (const key of POOL_KEYS) {
    clamped[key] = clampIndex(selection[key], pools?.[key].length ?? 0)
  }
  return clamped
}
